// frontend/src/pages/TimeTracking/ActivityManagement.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Form, Input, Select, Button, DatePicker, InputNumber, Typography,
  Row, Col, Card, Modal, message, Spin, Divider, Table, Space, Popconfirm, Tag
} from 'antd';
import {
  SendOutlined, RollbackOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined, UserOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import { getTimeEntriesByCR, addTimeEntry, updateTimeEntry, deleteTimeEntry } from '../../utils/timeTrackingService';
import './TimeTrackingForm.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ActivityManagement = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const notificationApi = useNotificationApi();
    
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [crInfo, setCrInfo] = useState(null);
    const [activities, setActivities] = useState([]);
    const [editingActivity, setEditingActivity] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    const crMasterId = searchParams.get('crMasterId');
    const dateFormat = 'DD/MM/YYYY';

    // Watch time components for auto-calculation
    const startHour = Form.useWatch('start_hour', form);
    const startMinute = Form.useWatch('start_minute', form);
    const startAmPm = Form.useWatch('start_ampm', form);
    const endHour = Form.useWatch('end_hour', form);
    const endMinute = Form.useWatch('end_minute', form);
    const endAmPm = Form.useWatch('end_ampm', form);

    // Calculate totals from activities
    const totals = useMemo(() => {
        if (!activities || activities.length === 0) {
            return {
                totalHours: 0,
                byType: {},
                byTypeSorted: []
            };
        }

        const byType = {};
        let totalHours = 0;

        activities.forEach(activity => {
            const duration = parseFloat(activity.duration) || 0;
            totalHours += duration;

            const type = activity.activity_type || 'Uncategorized';
            byType[type] = (byType[type] || 0) + duration;
        });

        // Sort by hours descending
        const byTypeSorted = Object.entries(byType)
            .sort((a, b) => b[1] - a[1])
            .map(([type, hours]) => ({ type, hours }));

        return {
            totalHours: parseFloat(totalHours.toFixed(2)),
            byType,
            byTypeSorted
        };
    }, [activities]);

    // Auto-calculate duration when time components change
    useEffect(() => {
        const allStartFilled = startHour !== undefined && startHour !== null && 
                              startMinute !== undefined && startMinute !== null && 
                              startAmPm;
        const allEndFilled = endHour !== undefined && endHour !== null && 
                            endMinute !== undefined && endMinute !== null && 
                            endAmPm;
        
        if (allStartFilled && allEndFilled) {
            let startHour24 = parseInt(startHour);
            let endHour24 = parseInt(endHour);
            
            if (startAmPm === 'PM' && startHour24 !== 12) startHour24 += 12;
            if (startAmPm === 'AM' && startHour24 === 12) startHour24 = 0;
            if (endAmPm === 'PM' && endHour24 !== 12) endHour24 += 12;
            if (endAmPm === 'AM' && endHour24 === 12) endHour24 = 0;
            
            const totalStartMinutes = startHour24 * 60 + parseInt(startMinute);
            const totalEndMinutes = endHour24 * 60 + parseInt(endMinute);
            const diffMinutes = totalEndMinutes - totalStartMinutes;
            
            if (diffMinutes > 0) {
                const hours = Math.floor(diffMinutes / 60);
                const minutes = diffMinutes % 60;
                const totalHours = parseFloat((hours + minutes / 60).toFixed(2));
                
                form.setFieldsValue({
                    hours: hours,
                    minutes: minutes,
                    duration: totalHours
                });
            } else if (diffMinutes < 0) {
                message.warning('End time must be after start time');
                form.setFieldsValue({ 
                    end_hour: undefined,
                    end_minute: undefined,
                    end_ampm: undefined,
                    hours: 0,
                    minutes: 0,
                    duration: 0
                });
            } else {
                form.setFieldsValue({
                    hours: 0,
                    minutes: 0,
                    duration: 0
                });
            }
        } else {
            form.setFieldsValue({
                hours: 0,
                minutes: 0,
                duration: 0
            });
        }
    }, [startHour, startMinute, startAmPm, endHour, endMinute, endAmPm, form]);

    const showError = useCallback((title, content, key = 'globalError') => {
        notificationApi.destroy(key);
        notificationApi.error({
            key,
            message: title,
            description: content,
            duration: 4.5,
            placement: 'topRight',
        });
    }, [notificationApi]);
    
    const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
        notificationApi.success({
            key,
            message: title,
            description,
            duration: 3,
            placement: 'topRight',
        });
    }, [notificationApi]);

    const fetchUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const response = await api.get('/api/users/active-internal-and-partner');
            if (response.data.success) {
                const filteredUsers = response.data.data.filter(user => 
                    user.userType !== 'customer'
                );
                setUsers(filteredUsers);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            showError('Failed to Load Users', 'Could not load user list for resources.');
        } finally {
            setLoadingUsers(false);
        }
    }, [showError]);

    useEffect(() => {
        const fetchData = async () => {
            if (!crMasterId) {
                setInitialLoading(false);
                return;
            }
            
            try {
                const crResponse = await api.get(`/api/time-tracking/cr/${crMasterId}`);
                if (crResponse.data.success) {
                    setCrInfo(crResponse.data.data);
                }
                
                const entriesResponse = await getTimeEntriesByCR(crMasterId);
                if (entriesResponse.success) {
                    setActivities(entriesResponse.data);
                }
                
                await fetchUsers();
            } catch (error) {
                console.error('Error fetching data:', error);
                showError('Failed to Load', 'Could not load activity data');
            } finally {
                setInitialLoading(false);
            }
        };
        
        fetchData();
    }, [crMasterId, showError, fetchUsers]);

    const handleSubmit = async (values) => {
        setModalLoading(true);
        
        const resources = Array.isArray(values.resource) 
            ? values.resource.join(', ') 
            : values.resource;
        
        let startTimeStr = null;
        let endTimeStr = null;
        
        if (values.start_hour !== undefined && values.start_minute !== undefined && values.start_ampm) {
            let hour = parseInt(values.start_hour);
            if (values.start_ampm === 'PM' && hour !== 12) hour += 12;
            if (values.start_ampm === 'AM' && hour === 12) hour = 0;
            startTimeStr = `${String(hour).padStart(2, '0')}:${String(values.start_minute).padStart(2, '0')}:00`;
        }
        
        if (values.end_hour !== undefined && values.end_minute !== undefined && values.end_ampm) {
            let hour = parseInt(values.end_hour);
            if (values.end_ampm === 'PM' && hour !== 12) hour += 12;
            if (values.end_ampm === 'AM' && hour === 12) hour = 0;
            endTimeStr = `${String(hour).padStart(2, '0')}:${String(values.end_minute).padStart(2, '0')}:00`;
        }
        
        const payload = {
            cr_master_id: parseInt(crMasterId),
            entry_date: values.entry_date ? values.entry_date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
            start_time: startTimeStr,
            end_time: endTimeStr,
            duration: values.duration,
            activity_type: values.activity_type,
            resource: resources,
            description: values.description || null
        };
        
        try {
            let response;
            if (editingActivity) {
                response = await updateTimeEntry(editingActivity.id, payload);
                if (response.success) {
                    showSuccess('Activity Updated', 'Activity has been updated successfully');
                }
            } else {
                response = await addTimeEntry(payload);
                if (response.success) {
                    showSuccess('Activity Added', 'New activity has been added successfully');
                }
            }
            
            if (response.success) {
                const entriesResponse = await getTimeEntriesByCR(crMasterId);
                if (entriesResponse.success) {
                    setActivities(entriesResponse.data);
                }
                setModalVisible(false);
                form.resetFields();
                setEditingActivity(null);
                form.setFieldsValue({
                    entry_date: null,
                    duration: 0,
                    hours: 0,
                    minutes: 0,
                    start_hour: undefined,
                    start_minute: undefined,
                    start_ampm: undefined,
                    end_hour: undefined,
                    end_minute: undefined,
                    end_ampm: undefined
                });
            } else {
                showError('Operation Failed', response.message || 'Failed to save activity');
            }
        } catch (error) {
            console.error('Error saving activity:', error);
            showError('Operation Failed', error.response?.data?.message || 'Failed to save activity');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingActivity(record);
        
        let resourceArray = [];
        if (record.resource) {
            resourceArray = record.resource.split(', ').filter(r => r.trim() !== '');
        }
        
        const duration = record.duration || 0;
        const hours = Math.floor(duration);
        const minutes = Math.round((duration - hours) * 60);
        
        let startHour = null;
        let startMinute = null;
        let startAmPm = null;
        let endHour = null;
        let endMinute = null;
        let endAmPm = null;
        
        if (record.start_time) {
            const startMoment = moment(record.start_time, 'HH:mm:ss');
            if (startMoment.isValid()) {
                const h = startMoment.hour();
                startHour = h % 12 || 12;
                startMinute = startMoment.minute();
                startAmPm = h >= 12 ? 'PM' : 'AM';
            }
        }
        
        if (record.end_time) {
            const endMoment = moment(record.end_time, 'HH:mm:ss');
            if (endMoment.isValid()) {
                const h = endMoment.hour();
                endHour = h % 12 || 12;
                endMinute = endMoment.minute();
                endAmPm = h >= 12 ? 'PM' : 'AM';
            }
        }
        
        const entryDate = record.entry_date ? moment(record.entry_date) : null;
        
        form.resetFields();
        form.setFieldsValue({
            entry_date: entryDate,
            start_hour: startHour,
            start_minute: startMinute,
            start_ampm: startAmPm,
            end_hour: endHour,
            end_minute: endMinute,
            end_ampm: endAmPm,
            duration: record.duration,
            hours: hours,
            minutes: minutes,
            activity_type: record.activity_type,
            resource: resourceArray,
            description: record.description
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const response = await deleteTimeEntry(id);
            if (response.success) {
                showSuccess('Activity Deleted', 'Activity has been deleted successfully');
                const entriesResponse = await getTimeEntriesByCR(crMasterId);
                if (entriesResponse.success) {
                    setActivities(entriesResponse.data);
                }
            } else {
                showError('Delete Failed', response.message || 'Failed to delete activity');
            }
        } catch (error) {
            console.error('Error deleting activity:', error);
            showError('Delete Failed', error.response?.data?.message || 'Failed to delete activity');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingActivity(null);
        form.resetFields();
        form.setFieldsValue({
            entry_date: null,
            duration: 0,
            hours: 0,
            minutes: 0,
            start_hour: undefined,
            start_minute: undefined,
            start_ampm: undefined,
            end_hour: undefined,
            end_minute: undefined,
            end_ampm: undefined
        });
        setModalVisible(true);
    };

    const renderResourceTags = (resources) => {
        if (!resources) return '-';
        const resourceList = resources.split(', ').filter(r => r.trim() !== '');
        if (resourceList.length === 0) return '-';
        
        return (
            <Space size={4} wrap>
                {resourceList.slice(0, 3).map((resource, index) => (
                    <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                        {resource}
                    </Tag>
                ))}
                {resourceList.length > 3 && (
                    <Tag color="default">+{resourceList.length - 3} more</Tag>
                )}
            </Space>
        );
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'entry_date',
            key: 'entry_date',
            width: 120,
            render: (date) => date ? moment(date).format(dateFormat) : '-'
        },
        {
            title: 'Start Time',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 100,
            render: (time) => time ? moment(time, 'HH:mm:ss').format('hh:mm A') : '-'
        },
        {
            title: 'End Time',
            dataIndex: 'end_time',
            key: 'end_time',
            width: 100,
            render: (time) => time ? moment(time, 'HH:mm:ss').format('hh:mm A') : '-'
        },
        {
            title: 'Duration (hrs)',
            dataIndex: 'duration',
            key: 'duration',
            width: 100,
            align: 'center',
            render: (value) => `${value} hrs`
        },
        {
            title: 'Activity Type',
            dataIndex: 'activity_type',
            key: 'activity_type',
            width: 150,
        },
        {
            title: 'Resource(s)',
            dataIndex: 'resource',
            key: 'resource',
            width: 200,
            render: (text) => renderResourceTags(text)
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            width: 250,
            render: (text) => text || '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button 
                        type="link" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this activity?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button 
                            type="link" 
                            danger 
                            icon={<DeleteOutlined />}
                            size="small"
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    if (!crMasterId) {
        return (
            <div className="time-tracking-page">
                <Card className="time-tracking-card">
                    <div className="empty-state">
                        <Title level={4}>No CR/CM Selected</Title>
                        <Text type="secondary">Please select a CR/CM to manage activities.</Text>
                        <div style={{ marginTop: 20 }}>
                            <Button onClick={() => navigate(-1)}>Go Back</Button>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (initialLoading) {
        return (
            <div className="time-tracking-page">
                <div className="time-tracking-loading">
                    <Spin size="large" tip="Loading..." />
                </div>
            </div>
        );
    }

    return (
        <div className="time-tracking-page">
            <div className="time-tracking-header">
                <Title level={2} className="time-tracking-title">
                    Activity Management - {crInfo?.cr_number || ''}
                </Title>
                <Text className="time-tracking-description">
                    Manage activities for {crInfo?.cr_number || 'this CR/CM'}. Each activity represents a specific work session.
                    Select hours, minutes, and AM/PM for start and end times. Duration will auto-calculate.
                </Text>
            </div>
            
            <Card className="time-tracking-card">
                <div className="time-tracking-content">
                    {crInfo && (
                        <div className="cr-info-box">
                            <div className="cr-info-title">CR/CM Information</div>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Text type="secondary">CR/CM #:</Text>
                                    <div><Text strong>{crInfo.cr_number}</Text></div>
                                </Col>
                                <Col span={16}>
                                    <Text type="secondary">Description:</Text>
                                    <div><Text strong>{crInfo.description}</Text></div>
                                </Col>
                            </Row>
                            
                            {/* NEW: Total Hours Section */}
                            <div style={{ 
                                marginTop: 12, 
                                paddingTop: 12, 
                                borderTop: '1px solid #e8e8e8',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        <ClockCircleOutlined style={{ marginRight: 6 }} />
                                        Total Hours:
                                    </Text>
                                    <Text strong style={{ fontSize: '16px', color: '#1890ff', marginLeft: '4px' }}>
                                        {totals.totalHours} hrs
                                    </Text>
                                </div>
                                
                                {totals.byTypeSorted.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                            Breakdown:
                                        </Text>
                                        {totals.byTypeSorted.map(({ type, hours }) => (
                                            <Tag 
                                                key={type} 
                                                color="blue" 
                                                style={{ 
                                                    fontSize: '12px',
                                                    padding: '2px 10px',
                                                    borderRadius: '12px',
                                                    marginBottom: '2px'
                                                }}
                                            >
                                                {type}: {hours} hrs
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <div style={{ marginBottom: 24, textAlign: 'right' }}>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />} 
                            onClick={handleAddNew}
                            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                        >
                            Log New Activity
                        </Button>
                    </div>
                    
                    <div className="existing-cr-list">
                        <div className="existing-cr-header">
                            <Title level={4} className="existing-cr-title">Activities</Title>
                            <Button onClick={() => navigate(-1)}>
                                Back to CR/CM
                            </Button>
                        </div>
                        
                        {activities.length > 0 ? (
                            <Table
                                columns={columns}
                                dataSource={activities}
                                rowKey="id"
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 1200 }}
                                loading={loading}
                            />
                        ) : (
                            <div className="empty-state">
                                <Text type="secondary">No activities found for this CR/CM.</Text>
                                <div style={{ marginTop: 16 }}>
                                    <Button type="primary" onClick={handleAddNew} icon={<PlusOutlined />}>
                                        Log First Activity
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="form-actions">
                        <Button icon={<RollbackOutlined />} onClick={() => navigate(-1)}>
                            Back to CR/CM
                        </Button>
                    </div>
                </div>
            </Card>
            
            <Modal
                title={editingActivity ? 'Edit Activity' : 'Log New Activity'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingActivity(null);
                    form.resetFields();
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form 
                    form={form} 
                    layout="vertical" 
                    onFinish={handleSubmit}
                    initialValues={{
                        hours: 0,
                        minutes: 0,
                        duration: 0,
                        start_hour: undefined,
                        start_minute: undefined,
                        start_ampm: undefined,
                        end_hour: undefined,
                        end_minute: undefined,
                        end_ampm: undefined
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="entry_date"
                                label="Date *"
                                rules={[{ required: true, message: 'Please select date' }]}
                            >
                                <DatePicker 
                                    style={{ width: '100%' }} 
                                    format={dateFormat}
                                    className="form-control"
                                    disabled={!!editingActivity}
                                    onChange={(date) => {
                                        form.setFieldsValue({ entry_date: date || null });
                                    }}
                                    placeholder="Select date"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="activity_type"
                                label="Type of Activity *"
                                rules={[{ required: true, message: 'Please select activity type' }]}
                            >
                                <Select placeholder="Select activity type">
                                    <Option value="Analysis">Analysis</Option>
                                    <Option value="Bug Fixes">Bug Fixes</Option>
                                    <Option value="Data Patching">Data Patching</Option>
                                    <Option value="Deployment">Deployment</Option>
                                    <Option value="Development">Development</Option>                                    
                                    <Option value="Documentation">Documentation</Option>
                                    <Option value="Final Acceptance Test">FAT</Option>
                                    <Option value="Infra Setup & Configuration">Infra Setup & Configuration</Option>
                                    <Option value="Meeting">Meeting</Option>
                                    <Option value="Patch Upgrade">Patch Upgrade</Option>
                                    <Option value="Review">Review</Option>
                                    <Option value="Security Hardening">Security Hardening</Option>
                                    <Option value="System Integration Test">SIT</Option>
                                    <Option value="Support">Support</Option>
                                    <Option value="Testing">Testing</Option>
                                    <Option value="User Acceptance Test">UAT</Option>
                                    <Option value="URS">User Requirement Study / User Story</Option>
                                    <Option value="Other">Other</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <div style={{ marginBottom: 8 }}>
                        <Text strong>Start Time</Text>
                    </div>
                    <Row gutter={8}>
                        <Col span={8}>
                            <Form.Item
                                name="start_hour"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="HH" className="form-control" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                        <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text strong>:</Text>
                        </Col>
                        <Col span={7}>
                            <Form.Item
                                name="start_minute"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="MM" className="form-control" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                        <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="start_ampm"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="AM/PM" className="form-control" size="large">
                                    <Option value="AM">AM</Option>
                                    <Option value="PM">PM</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <div style={{ marginBottom: 8, marginTop: 8 }}>
                        <Text strong>End Time</Text>
                    </div>
                    <Row gutter={8}>
                        <Col span={8}>
                            <Form.Item
                                name="end_hour"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="HH" className="form-control" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                        <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text strong>:</Text>
                        </Col>
                        <Col span={7}>
                            <Form.Item
                                name="end_minute"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="MM" className="form-control" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                        <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="end_ampm"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="AM/PM" className="form-control" size="large">
                                    <Option value="AM">AM</Option>
                                    <Option value="PM">PM</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <div style={{ marginBottom: 8, marginTop: 16 }}>
                        <Text strong>Calculated Duration</Text>
                    </div>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="hours" label="Hours">
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    className="form-control"
                                    size="large"
                                    disabled
                                    placeholder="Auto"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="minutes" label="Minutes">
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    className="form-control"
                                    size="large"
                                    disabled
                                    placeholder="Auto"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="duration"
                                label="Duration (hours)"
                                rules={[{ required: true, message: 'Duration is required' }]}
                            >
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    className="form-control"
                                    size="large"
                                    disabled
                                    placeholder="Auto-calculated"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="resource"
                                label="Resource(s) *"
                                rules={[{ required: true, message: 'Please select at least one resource' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select one or more resources"
                                    loading={loadingUsers}
                                    showSearch
                                    optionFilterProp="children"
                                    allowClear
                                    className="form-control"
                                    size="large"
                                    maxTagCount="responsive"
                                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                                    filterOption={(input, option) => {
                                        const children = option?.children?.toString() || '';
                                        return children.toLowerCase().includes(input.toLowerCase());
                                    }}
                                    tagRender={(props) => {
                                        const { label, closable, onClose } = props;
                                        return (
                                            <Tag 
                                                color="blue" 
                                                closable={closable} 
                                                onClose={onClose}
                                                style={{ marginRight: 3, marginBottom: 3 }}
                                            >
                                                <UserOutlined style={{ marginRight: 4 }} />
                                                {label}
                                            </Tag>
                                        );
                                    }}
                                    dropdownStyle={{ minWidth: 300 }}
                                >
                                    {users.map(user => (
                                        <Option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                            {user.firstName} {user.lastName} - {user.email}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Form.Item name="description" label="Description">
                        <TextArea rows={3} placeholder="Describe the work done..." />
                    </Form.Item>
                    
                    <Divider />
                    
                    <div className="form-actions" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                        <Button onClick={() => { 
                            setModalVisible(false); 
                            setEditingActivity(null); 
                            form.resetFields(); 
                        }}>
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={modalLoading}
                            icon={<SendOutlined />}
                        >
                            {editingActivity ? 'Update Activity' : 'Save Activity'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default ActivityManagement;